import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import { useAuth } from '../../Contexts/AuthContext';
import { useToast } from '../../Contexts/ToastContext';
import { useCompanyProfile } from '../../Contexts/CompanyProfileContext';
import { updateCustomerProfile, uploadProfilePicture } from '../../Services/customerService';
import { getOrdersByEmail, getStatusColor, TrackedOrder } from '../../Services/ordertrackingservice';

const CameraIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
    </svg>
);

// ── First + last initials, uppercased, for the placeholder avatar ───────────
const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const accountStyles = `
  .account-card {
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 8px;
    box-shadow: 0 2px 6px rgba(20,20,43,0.04), 0 12px 32px rgba(20,20,43,0.06);
    position: relative;
    overflow: hidden;
  }
  .account-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #717fe0, #a78bfa);
  }
  .account-avatar-wrap {
    position: relative;
    width: 96px;
    height: 96px;
    flex-shrink: 0;
  }
  .account-avatar-img,
  .account-avatar-fallback {
    width: 96px;
    height: 96px;
    border-radius: 50%;
    object-fit: cover;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #ececec;
  }
  .account-avatar-fallback {
    background: linear-gradient(135deg, #717fe0, #a78bfa);
    color: #fff;
    font-family: 'Poppins-Medium', sans-serif;
    font-size: 30px;
    letter-spacing: 0.5px;
  }
  .account-avatar-edit-btn {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #232323;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid #fff;
    cursor: pointer;
    transition: background 0.25s ease, transform 0.2s ease;
  }
  .account-avatar-edit-btn:hover {
    background: #717fe0;
    transform: scale(1.06);
  }
  .account-avatar-edit-btn[aria-busy="true"] {
    pointer-events: none;
    opacity: 0.6;
  }
  .account-field-label {
    display: block;
    font-family: 'Poppins-Medium', sans-serif;
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #999;
    margin-bottom: 8px;
  }
  .account-signout-link {
    background: none;
    border: none;
    padding: 0;
    font-family: 'Poppins-Medium', sans-serif;
    font-size: 13px;
    color: #888;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
    transition: color 0.2s ease;
  }
  .account-signout-link:hover {
    color: #f74877;
  }
  .account-order-card {
    border: 1px solid #ececec;
    border-radius: 6px;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;
  }
  .account-order-card:hover {
    border-color: #d8dbf5;
    box-shadow: 0 4px 16px rgba(113,127,224,0.12);
  }
`;

const Account: React.FC = () => {
    const { currentUser, customerProfile, loading, logout, refreshProfile } = useAuth();
    const { showToast } = useToast();
    const { formatPrice } = useCompanyProfile();
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const [orders, setOrders] = useState<TrackedOrder[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(true);

    useEffect(() => {
        if (customerProfile) {
            setName(customerProfile.name);
            setPhone(customerProfile.phone || '');
        }
    }, [customerProfile]);

    useEffect(() => {
        if (!customerProfile?.email) return;
        setOrdersLoading(true);
        getOrdersByEmail(customerProfile.email)
            .then(setOrders)
            .finally(() => setOrdersLoading(false));
    }, [customerProfile?.email]);

    if (loading) {
        return (
            <div>
                <Header />
                <div className="txt-center p-tb-100">
                    <p className="stext-111 cl2">Loading...</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (!currentUser) {
        return <Navigate to="/login" state={{ from: '/account' }} replace />;
    }

    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            showToast('Please enter your name', 'error');
            return;
        }
        setSavingProfile(true);
        try {
            await updateCustomerProfile(currentUser.uid, { name: name.trim(), phone: phone.trim() });
            await refreshProfile();
            showToast('Profile updated successfully', 'success');
        } catch (error) {
            console.error('Error updating profile:', error);
            showToast('Failed to update profile', 'error');
        } finally {
            setSavingProfile(false);
        }
    };

    const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            showToast('Please choose an image file', 'error');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            showToast('Image must be smaller than 5MB', 'error');
            return;
        }

        const localUrl = URL.createObjectURL(file);
        setPreviewUrl(localUrl);
        setUploadingPhoto(true);
        try {
            const url = await uploadProfilePicture(currentUser.uid, file, customerProfile?.photoURL);
            await updateCustomerProfile(currentUser.uid, { photoURL: url });
            await refreshProfile();
            showToast('Profile picture updated', 'success');
        } catch (error) {
            console.error('Error uploading profile picture:', error);
            showToast('Failed to upload profile picture', 'error');
        } finally {
            setUploadingPhoto(false);
            setPreviewUrl(null);
            URL.revokeObjectURL(localUrl);
        }
    };

    const handleLogout = async () => {
        await logout();
        showToast('You have been signed out', 'success');
        navigate('/');
    };

    const avatarSrc = previewUrl || customerProfile?.photoURL;

    return (
        <div>
            <style>{accountStyles}</style>
            <Header />

            <section className="bg-img1 txt-center p-lr-15 p-tb-92" style={{ backgroundImage: `url('asset/images/bg-11.png')` }}>
                <h2 className="ltext-105 cl0 txt-center">My Account</h2>
            </section>

            <section className="bg0 p-t-104 p-b-116">
                <div className="container">
                    <div className="flex-w flex-tr" style={{ gap: 30 }}>
                        {/* Profile */}
                        <div className="account-card p-lr-50 p-t-55 p-b-55 p-lr-15-lg w-full-md" style={{ flex: '1 1 420px' }}>
                            <h4 className="mtext-105 cl2 p-b-30">Profile Details</h4>

                            {/* Avatar */}
                            <div className="flex-w" style={{ alignItems: 'center', gap: 20, marginBottom: 34 }}>
                                <div className="account-avatar-wrap">
                                    {avatarSrc ? (
                                        <img src={avatarSrc} alt={name || 'Profile'} className="account-avatar-img" style={{ opacity: uploadingPhoto ? 0.5 : 1 }} />
                                    ) : (
                                        <div className="account-avatar-fallback">{getInitials(name || customerProfile?.email || '?')}</div>
                                    )}
                                    <button
                                        type="button"
                                        className="account-avatar-edit-btn"
                                        aria-busy={uploadingPhoto}
                                        onClick={() => fileInputRef.current?.click()}
                                        title="Change profile picture"
                                    >
                                        <CameraIcon />
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handlePhotoChange}
                                    />
                                </div>
                                <div>
                                    <p className="stext-111 cl2" style={{ fontWeight: 600, fontSize: 15 }}>
                                        {name || 'Your Name'}
                                    </p>
                                    <p className="stext-115 cl6" style={{ marginTop: 4 }}>
                                        {uploadingPhoto ? 'Uploading photo...' : 'JPG or PNG, up to 5MB'}
                                    </p>
                                </div>
                            </div>

                            <form onSubmit={handleSaveProfile}>
                                <div className="m-b-20">
                                    <span className="account-field-label">Full Name</span>
                                    <div className="bor8">
                                        <input
                                            className="stext-111 cl2 plh3 size-116 p-lr-30"
                                            type="text"
                                            placeholder="Your Full Name"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                            disabled={savingProfile}
                                        />
                                    </div>
                                </div>

                                <div className="m-b-20">
                                    <span className="account-field-label">Email Address</span>
                                    <div className="bor8" style={{ background: '#fafafa' }}>
                                        <input
                                            className="stext-111 cl6 plh3 size-116 p-lr-30"
                                            type="email"
                                            value={customerProfile?.email || ''}
                                            disabled
                                        />
                                    </div>
                                </div>

                                <div className="m-b-30">
                                    <span className="account-field-label">Phone Number</span>
                                    <div className="bor8">
                                        <input
                                            className="stext-111 cl2 plh3 size-116 p-lr-30"
                                            type="tel"
                                            placeholder="Your Contact Number"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            disabled={savingProfile}
                                        />
                                    </div>
                                </div>

                                <div className="flex-w" style={{ alignItems: 'center', gap: 24 }}>
                                    <button
                                        type="submit"
                                        className="flex-c-m stext-101 cl0 size-121 bg3 bor1 hov-btn3 p-lr-15 trans-04 pointer"
                                        disabled={savingProfile}
                                    >
                                        {savingProfile ? 'Saving...' : 'Save Changes'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleLogout}
                                        className="account-signout-link"
                                    >
                                        Sign Out
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Order History */}
                        <div className="account-card p-lr-50 p-t-55 p-b-55 p-lr-15-lg w-full-md" style={{ flex: '1 1 420px' }}>
                            <h4 className="mtext-105 cl2 p-b-30">Order History</h4>

                            {ordersLoading ? (
                                <p className="stext-111 cl6">Loading your orders...</p>
                            ) : orders.length === 0 ? (
                                <p className="stext-111 cl6">
                                    You haven't placed any orders yet. Browse our{' '}
                                    <a href="/shop" className="cl1" style={{ fontWeight: 600 }}>shop</a> to get started.
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    {orders.map((order) => (
                                        <div
                                            key={order.id}
                                            className="account-order-card p-15"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/order-confirmation/${order.id}`)}
                                        >
                                            <div className="flex-w" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                                                <div>
                                                    <span className="stext-111 cl2" style={{ fontWeight: 600 }}>
                                                        #{order.orderNumber}
                                                    </span>
                                                    <p className="stext-115 cl6" style={{ marginTop: 4 }}>
                                                        {order.orderDate.toLocaleDateString('en-US', {
                                                            year: 'numeric', month: 'short', day: 'numeric',
                                                        })}
                                                        {' · '}
                                                        {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                                                    </p>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <p className="stext-111 cl2" style={{ fontWeight: 600 }}>
                                                        {formatPrice(order.totalAmount)}
                                                    </p>
                                                    <span
                                                        className="stext-115"
                                                        style={{
                                                            color: getStatusColor(order.status),
                                                            textTransform: 'capitalize',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {order.status}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Account;
